import { PGS, Whatuni } from "@packages/constants/projects.const";
import { v4 as uuidv4 } from "uuid";
import { DataLayerGA4AttrType, GA4EventAttrType } from "../types/datalayerGA";
import {
  GA4DataLayer,
  GA4DataLayerFn,
  getCookie,
} from "./helper-function";
import { logClickstreamEvent } from "./clickstream";
import { GAConstants } from "@packages/shared-components/services/constants";

export function getCookieValue(name: any) {
  if (typeof document === "undefined") {
    return ""; // Return empty if not in the browser
  }

  const cookieArray = document?.cookie.split("; ");
  const cookie = cookieArray.find((c) => c.startsWith(`${name}=`));
  return cookie ? cookie.split("=")[1] : "";
}

// export function setNewCookie(cookieString: string) {
//   if (typeof document !== "undefined") {
//     document.cookie = cookieString += "; Secure=true";
//   }
// }

export function ordinarySuffix(rank: any): string {
  const j = rank % 10;
  const k = rank % 100;
  if (j === 1 && k !== 11) return `${String(rank)}st`;

  if (j === 2 && k !== 12) return `${String(rank)}nd`;

  if (j === 3 && k !== 13) return `${String(rank)}rd`;

  return `${String(rank)}th`;
}

export function poundCostCommaSeparation(cost: number = 0) {
  const formattedCost = cost?.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }); // Output sample: "£1,234.56"
  return formattedCost;
}

export function isWhatuni() {
  return process.env.PROJECT === Whatuni.projectName;
}

export function isPGS() {
  return process.env.PROJECT === PGS.projectName;
}

export function getProjectSpecificData(
  propertyKeyName: string,
  project?: "Whatuni" | "PGS"
) {
  const projectData: any = { Whatuni, PGS };
  return projectData[(project ?? process.env.PROJECT)!]?.[propertyKeyName];
}

export function getCommonAPIHeaders() {
  return {
    "Content-Type": "application/json",
    "x-correlation-id": uuidv4(),
    "x-api-key": `${process.env.NEXT_PUBLIC_X_API_KEY}`,
  };
}

export async function gaForCtaClicks(
  enquiryProps: any,
  formtype: string,
  commonGAAttrs: any,
  cpeFlag?: any,
  digitalOrNonDigital: string = ""
) {
  const cpeOrTenancyType = cpeFlag === "Y" ? "cpe" : "tenancy";
  const digitalOrNonDigitalFlag =
    digitalOrNonDigital === "Y"
      ? "digital"
      : digitalOrNonDigital === "N"
      ? "physical"
      : "na";

  const providerType =
    enquiryProps?.sponsoredListingFlag === "Y" || enquiryProps?.subOrderItemId
      ? "advertiser"
      : "non_advertiser";

  const sponsoredSr =
    enquiryProps?.sponsoredListingFlag === "Y"
      ? "Yes"
        : "";
  const gaData: GA4EventAttrType = {
    event: GAConstants.revenue_kpi,
    event_name: formtype,
    institution_id: enquiryProps?.collegeId,
    institution_name: enquiryProps?.collegeDisplayName,
    course_name: enquiryProps?.courseName,
    provider_type: providerType,
    ucas_points: enquiryProps?.ucasPoints,
    cycle_phase: localStorage.getItem("clearing") || "",
    page_name: localStorage.getItem("gaPageName") || "",
    revenue_model : cpeOrTenancyType,
    cta_url : enquiryProps?.url,
    revenue_value : enquiryProps?.revenueValue,
    sponsored : sponsoredSr,
    form_type: digitalOrNonDigitalFlag,
   ...commonGAAttrs,
  };

  return GA4DataLayer(gaData);
}

export async function oldGAForCtaClicks(
  enquiryProps: any,
  cta_name: string,
  cta_url: string,
  formtype: string,
  commonGAAttrs: any,
  cpeFlag?: any,
  digitalOrNonDigital: string = ""
) {
  const cpeOrTenancyType = cpeFlag === "Y" ? "cpe" : "tenancy";
  const digitalOrNonDigitalFlag =
    digitalOrNonDigital === "N"
      ? "non_digital_opted"
      : digitalOrNonDigital
        ? "digital_opted"
        : "";

  const providerType =
    enquiryProps?.sponsoredListingFlag === "Y" || enquiryProps?.subOrderItemId
      ? "advertiser"
      : "non_advertiser";

  const sponsoredSr =
    enquiryProps?.sponsoredListingFlag === "Y"
      ? "sponsored"
      : enquiryProps?.manualBoostingFlag === "Y"
        ? "manual_boosting"
        : "";
   const dataLabel2 =
    formtype === "prospectus_request"
      ? digitalOrNonDigitalFlag
      : process.env.PROJECT === "Whatuni"
        ? cpeOrTenancyType
        : "";

  const dataLabel4 =
    process.env.PROJECT === "PGS" || formtype === "prospectus_request"
      ? cpeOrTenancyType
      : "";
  const gaData: DataLayerGA4AttrType = {
    event: enquiryProps.event,
    eventName: formtype,
    college_id: enquiryProps?.collegeId,
    college_name: enquiryProps?.collegeDisplayName,
    course_name: enquiryProps?.courseName,
    provider_type: providerType,
    ucas_points: enquiryProps?.ucasPoints,
    clearing: localStorage.getItem("clearing") || "",
    page_name: localStorage.getItem("gaPageName") || "",
    sponsored : sponsoredSr,
    //form_type: digitalOrNonDigitalFlag,
    data_label2: dataLabel2,
    data_label4: dataLabel4,
    cta_name: enquiryProps?.cta_name || cta_name,
    cta_url: cta_url  || "",
    study_mode : enquiryProps?.study_mode || commonGAAttrs?.study_mode,
    ...{
      ...(enquiryProps?.cpe_parent_subject && {
        cpe_parent_subject: "NA",
      }),
      ...(enquiryProps?.cpe_child_subject && {
        cpe_child_subject: "NA",
      }),
    },
   ...commonGAAttrs,
  };

  return GA4DataLayerFn(gaData);
}

function isValidAndNotNA(value: any) {
  return value && value !== "NA" ? value : "";
}

export async function clickstreamForClicks(
  enquiryProps: any,
  csData: any,
  commonGAAttrs: any,
  digitalFlag = "",
  clearing?: Boolean
) {
  csData.browseNodeSubjects = enquiryProps?.browseNodeSubjects || commonGAAttrs?.subject_area || commonGAAttrs?.subject_discipline;
  csData.pageName = localStorage.getItem("gaPageName");
  csData.idpcCourseId = enquiryProps?.courseId;
  csData.idpcInstitutionId =
    enquiryProps?.collegeId || enquiryProps?.idpcInstitutionId;
  csData.subOrderItemId = enquiryProps?.subOrderItemId;
  csData.displayOrder = enquiryProps?.displayIndex
    ? String(enquiryProps?.displayIndex)
    : "NA";
  csData.studyLevel = enquiryProps?.studyLevel || commonGAAttrs?.study_level;
  csData.childStudyLevel =
    enquiryProps?.childStudyLevel || commonGAAttrs?.child_study_level;
  csData.childStudyLevelId =
    enquiryProps?.childStudyLevelId || commonGAAttrs?.child_study_level_id;
  csData.studyMode = isValidAndNotNA(commonGAAttrs?.study_mode);
  csData.subject =
    enquiryProps?.subject ||
    isValidAndNotNA(commonGAAttrs?.subject_area) ||
    isValidAndNotNA(commonGAAttrs?.subject_discipline);
  csData.paginationNumber =
    typeof commonGAAttrs?.data_label5 === "number"
      ? commonGAAttrs.data_label5
      : (enquiryProps?.paginationNumber ?? "");
  csData.networkId = clearing
    ? "7"
    : commonGAAttrs?.study_level?.toLowerCase() === "postgraduate"
      ? "3"
      : "2";
  csData.idpcCourseName = enquiryProps?.courseName;
  csData.orderItemId = enquiryProps?.orderItemId;
  // csData.campaignId = enquiryProps?.campaignId;
  csData.functionalityName =
    csData.functionalityName || enquiryProps?.functionalityName;
  if (csData.functionalityName !== "Reviews") {
    csData.isAdvertiser =
      enquiryProps?.subOrderItemId || enquiryProps?.sponsoredListingFlag === "Y"
        ? "Y"
        : "N";
    csData.postFlag = digitalFlag !== "Y" ? "No" : "Yes";
    csData.downloadFlag = digitalFlag === "Y" ? "Yes" : "No";
    csData.sectionName = enquiryProps?.sectionName;
    csData.studyLevelId = enquiryProps?.studyLevelId;
    csData.interestedIntakeYear = getCookie("yearOfEntry") || "";
  }
  return await logClickstreamEvent(csData);
}

export const initCaps = (str: string) => {
  if (!str) return "";
  return str?.charAt(0).toUpperCase() + str?.slice(1);
};

export const toTitleFromSlug = (str: string) =>
  str?.split("-")?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase())?.join(" ");
